// frontend/src/components/wizard/JoinRulePicker.jsx
/**
 * JoinRulePicker — Selector compacto de reglas para claves JOIN.
 *
 * Subset del ruleCatalog de StepMapping, filtrado a reglas con sentido
 * sobre claves de relación (normalización de texto, numérico, fechas,
 * hash). Excluye reglas de contenido (SET_FIXED, IF_ELSE, DEFAULT_NULL).
 *
 * Props:
 *   rules     : array — pipeline activo [{id, params}], igual que mapping.pipeline
 *   onChange  : (newRules: array) => void — callback al cambiar reglas
 *   colName   : string — nombre de la columna (para el header del picker)
 *   disabled  : bool  — bloquear si la columna aún no está seleccionada
 *
 * Las reglas se almacenan en sourceRelationships[n].rulesFrom / rulesTo
 * y el worker (join_engine.py) las aplica via _apply_key_rules() antes
 * del hash join — exactamente el mismo pipeline que transformer.py.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Settings2, X, CheckCircle2 } from 'lucide-react';
import { useLanguageStore } from '../../store/useLanguageStore';

// ── Catálogo de reglas válidas para claves JOIN ───────────────────────
// id debe coincidir EXACTAMENTE con los rule_id de transformer.py
const JOIN_RULE_CATALOG = [
  // ── Texto ──────────────────────────────────────────────────────────
  {
    id: 'TRIM_EDGES',
    emoji: '✂️',
    labelKey: 'wizard.mapping.rule_names.TRIM_EDGES',
    labelFallback: 'Quitar espacios',
    params: null,
  },
  {
    id: 'UPPERCASE',
    emoji: '🔠',
    labelKey: 'wizard.mapping.rule_names.UPPERCASE',
    labelFallback: 'Mayúsculas',
    params: null,
  },
  {
    id: 'LOWERCASE',
    emoji: '🔡',
    labelKey: 'wizard.mapping.rule_names.LOWERCASE',
    labelFallback: 'Minúsculas',
    params: null,
  },
  {
    id: 'REMOVE_SPECIAL_CHARS',
    emoji: '🧹',
    labelKey: 'wizard.mapping.rule_names.REMOVE_SPECIAL_CHARS',
    labelFallback: 'Quitar caracteres especiales',
    params: null,
  },
  {
    id: 'COLLAPSE_SPACES',
    emoji: '⬛',
    labelKey: 'wizard.mapping.rule_names.COLLAPSE_SPACES',
    labelFallback: 'Colapsar espacios múltiples',
    params: null,
  },
  // ── Numérico ───────────────────────────────────────────────────────
  {
    id: 'ONLY_NUMBERS',
    emoji: '🔢',
    labelKey: 'wizard.mapping.rule_names.ONLY_NUMBERS',
    labelFallback: 'Solo números (quitar todo lo demás)',
    params: null,
  },
  {
    id: 'TO_INT',
    emoji: '💯',
    labelKey: 'wizard.mapping.rule_names.TO_INT',
    labelFallback: 'Convertir a entero',
    params: null,
  },
  // ── Con parámetros ─────────────────────────────────────────────────
  {
    id: 'ADD_PREFIX',
    emoji: '◀️',
    labelKey: 'wizard.mapping.rule_names.ADD_PREFIX',
    labelFallback: 'Agregar prefijo',
    params: [{ key: 'text', placeholder: 'Ej: CLI-', label: 'Prefijo' }],
  },
  {
    id: 'ADD_SUFFIX',
    emoji: '▶️',
    labelKey: 'wizard.mapping.rule_names.ADD_SUFFIX',
    labelFallback: 'Agregar sufijo',
    params: [{ key: 'text', placeholder: 'Ej: -PE', label: 'Sufijo' }],
  },
  {
    id: 'REPLACE',
    emoji: '🔄',
    labelKey: 'wizard.mapping.rule_names.REPLACE',
    labelFallback: 'Reemplazar texto',
    params: [
      { key: 'search', placeholder: 'Buscar...', label: 'Buscar' },
      { key: 'replace', placeholder: 'Reemplazar por', label: 'Por' },
    ],
  },
  // ── Fecha ──────────────────────────────────────────────────────────
  {
    id: 'FORMAT_DATE',
    emoji: '📅',
    labelKey: 'wizard.mapping.rule_names.FORMAT_DATE',
    labelFallback: 'Normalizar fecha',
    params: 'date_format',   // especial: usa selects de formato
  },
  // ── Seguridad ──────────────────────────────────────────────────────
  {
    id: 'HASH_DATA',
    emoji: '🔒',
    labelKey: 'wizard.mapping.rule_names.HASH_DATA',
    labelFallback: 'Hash / Anonimizar (SHA-256)',
    params: null,
  },
];

const DATE_FORMATS_FROM = [
  { value: 'AUTO', label: 'Auto-detectar' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  { value: 'UNIX_S', label: 'Unix (segundos)' },
  { value: 'UNIX_MS', label: 'Unix (miliseg.)' },
];

const DATE_FORMATS_TO = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD  (ISO 8601)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYYMMDD', label: 'YYYYMMDD  (compacto)' },
  { value: 'UNIX_S', label: 'Unix (segundos)' },
];


// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════

export function JoinRulePicker({ rules = [], onChange, colName = '', disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);
  const { t } = useLanguageStore();

  // ── Cerrar con click fuera ────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // ── Helpers de pipeline ───────────────────────────────────────────
  const isActive = (ruleId) => rules.some(r => r.id === ruleId);

  const getRule = (ruleId) => rules.find(r => r.id === ruleId) || null;

  const toggleRule = (ruleDef) => {
    if (isActive(ruleDef.id)) {
      onChange(rules.filter(r => r.id !== ruleDef.id));
    } else {
      // Inicializar params vacíos según el tipo
      let initParams = {};
      if (Array.isArray(ruleDef.params)) {
        ruleDef.params.forEach(p => { initParams[p.key] = ''; });
      } else if (ruleDef.params === 'date_format') {
        initParams = { from: 'AUTO', to: 'YYYY-MM-DD' };
      }
      onChange([...rules, { id: ruleDef.id, params: initParams }]);
    }
  };

  const updateParam = (ruleId, key, value) => {
    onChange(rules.map(r =>
      r.id === ruleId ? { ...r, params: { ...r.params, [key]: value } } : r
    ));
  };

  const activeCount = rules.length;

  return (
    <div className="relative inline-block" ref={pickerRef}>

      {/* ── Trigger button ───────────────────────────────────────── */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(v => !v)}
        title={
          disabled
            ? 'Selecciona la columna primero'
            : activeCount > 0
              ? `${activeCount} regla${activeCount > 1 ? 's' : ''} activa${activeCount > 1 ? 's' : ''} sobre la clave`
              : 'Aplicar reglas sobre esta clave JOIN'
        }
        className={`mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-bold
          transition-all duration-150 select-none
          ${disabled
            ? 'opacity-30 cursor-not-allowed bg-slate-50 border-slate-200 text-slate-400'
            : activeCount > 0
              ? 'bg-indigo-100 border-indigo-300 text-indigo-700 hover:bg-indigo-200 shadow-sm'
              : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-600 hover:border-slate-300'
          }`}
      >
        <Settings2 className="w-3 h-3" />
        {activeCount > 0 ? (
          <span className="flex items-center gap-0.5">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            {activeCount}
          </span>
        ) : (
          <span>f(x)</span>
        )}
      </button>

      {/* ── Panel desplegable ─────────────────────────────────────── */}
      {isOpen && (
        <div
          className="absolute z-50 top-full mt-1 left-0 w-72 bg-white border border-slate-200
            rounded-xl shadow-2xl overflow-hidden"
          style={{ minWidth: '17rem' }}
        >

          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 border-b border-indigo-100">
            <span className="text-xs font-bold text-indigo-800 flex items-center gap-1.5">
              <Settings2 className="w-3.5 h-3.5" />
              Reglas clave{colName ? `: ${colName}` : ''}
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-indigo-400 hover:text-indigo-700 p-0.5 rounded transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Reglas */}
          <div className="max-h-80 overflow-y-auto p-2 space-y-1 custom-scrollbar">
            {JOIN_RULE_CATALOG.map((ruleDef) => {
              const active = isActive(ruleDef.id);
              const ruleData = getRule(ruleDef.id);

              return (
                <div
                  key={ruleDef.id}
                  className={`rounded-lg border transition-colors
                    ${active
                      ? 'border-indigo-200 bg-indigo-50'
                      : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                    }`}
                >
                  {/* Toggle row */}
                  <label className="flex items-center gap-2 px-2 py-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleRule(ruleDef)}
                      className="w-3.5 h-3.5 accent-indigo-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-base leading-none">{ruleDef.emoji}</span>
                    <span className={`text-xs font-medium leading-tight ${active ? 'text-indigo-800' : 'text-slate-700'}`}>
                      {t(ruleDef.labelKey) || ruleDef.labelFallback}
                    </span>
                  </label>

                  {/* Parámetros inline cuando está activa ──────── */}
                  {active && ruleDef.params && (

                    <div className="px-2 pb-2 space-y-1.5">

                      {/* Array de params simples (ADD_PREFIX, ADD_SUFFIX, REPLACE) */}
                      {Array.isArray(ruleDef.params) && ruleDef.params.map(p => (
                        <div key={p.key} className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-500 w-12 flex-shrink-0 font-medium uppercase tracking-wide">
                            {p.label}
                          </span>
                          <input
                            type="text"
                            value={ruleData?.params?.[p.key] || ''}
                            onChange={(e) => updateParam(ruleDef.id, p.key, e.target.value)}
                            placeholder={p.placeholder}
                            className="flex-1 text-xs border border-indigo-200 rounded px-2 py-1
                              focus:outline-none focus:border-indigo-400 bg-white placeholder-slate-300"
                          />
                        </div>
                      ))}

                      {/* Formato de fecha */}
                      {ruleDef.params === 'date_format' && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-500 w-12 flex-shrink-0 font-medium uppercase tracking-wide">
                              De
                            </span>
                            <select
                              value={ruleData?.params?.from || 'AUTO'}
                              onChange={(e) => updateParam(ruleDef.id, 'from', e.target.value)}
                              className="flex-1 text-xs border border-indigo-200 rounded px-1.5 py-1
                                focus:outline-none focus:border-indigo-400 bg-white"
                            >
                              {DATE_FORMATS_FROM.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-500 w-12 flex-shrink-0 font-medium uppercase tracking-wide">
                              A
                            </span>
                            <select
                              value={ruleData?.params?.to || 'YYYY-MM-DD'}
                              onChange={(e) => updateParam(ruleDef.id, 'to', e.target.value)}
                              className="flex-1 text-xs border border-indigo-200 rounded px-1.5 py-1
                                focus:outline-none focus:border-indigo-400 bg-white"
                            >
                              {DATE_FORMATS_TO.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              {activeCount === 0
                ? 'Sin reglas — clave usada tal cual'
                : `${activeCount} regla${activeCount > 1 ? 's' : ''} aplicada${activeCount > 1 ? 's' : ''} en secuencia`}
            </span>
            {activeCount > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-[10px] text-red-400 hover:text-red-600 font-medium transition-colors"
              >
                Limpiar
              </button>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
